<template>
  <div class="dashboard-container">
    <h1>Dashboard</h1>

    <div class="widgets-grid">
      <div
        v-for="widget in visibleWidgets"
        :key="widget.id"
        class="widget clickable"
        :class="{
          'active-widget': selectedWidget === widget.id,
          'loading-widget': loadingCounts
        }"
        @click="toggleSection(widget.id)"
      >
        <h6>{{ widget.title }}</h6>
        <div v-if="loadingCounts" class="widget-loading">
          <div class="spinner-border spinner-border-sm" />
        </div>
        <p v-else>{{ widget.count }}</p>
      </div>
    </div>

    <div v-if="errors.length" class="alert alert-warning mt-3">
      <p><strong>Note:</strong> Some data may be incomplete. Please refresh to try again.</p>
      <ul v-if="showDetailedErrors" class="error-list">
        <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
      </ul>
    </div>

    <!-- Dynamic component loading for better performance -->
    <div v-if="activeSection" class="content-section">
      <div v-if="loadingCounts" class="d-flex justify-content-center my-3">
        <div class="spinner-border" />
      </div>
      <template v-else>
        <h3 v-if="isStatusSection" class="section-title">Recent {{ activeSection.replace(/([A-Z])/g, " $1").trim() }}</h3>
        <component
  :is="getComponentForSection"
  :isDashboard="true"
  v-bind="isStatusSection ? {
    selectedStatuses: getStatusValue(activeSection),
    initialStatusFilter: getStatusValue(activeSection)
  } : {}"
/>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, defineAsyncComponent } from "vue"
import { useAuthStore } from "@/stores/auth"
import { DeliveryStatus } from "@/api/deliveries"
import dashboardApi, { Counts } from "@/api/dashboard"

// Lazy load components for better initial page load
const StoresView = defineAsyncComponent(() => import("@/views/StoresView.vue"))
const UsersView = defineAsyncComponent(() => import("@/views/UsersView.vue"))
const DeliveriesView = defineAsyncComponent(() => import("@/views/DeliveriesView.vue"))
const DriversView = defineAsyncComponent(() => import("@/views/DriversView.vue"))

const authStore = useAuthStore()

// Dashboard data with shallow references to avoid deep reactivity overhead
const counts = ref({
  totalStores: 0,
  totalUsers: 0,
  totalDrivers: 0,
  totalDeliveries: 0,
  deliveries: {}
} as Counts)

// Individual loading states for each widget
const loadingCounts = ref(false)

const errors = ref([])
const showDetailedErrors = ref(false)

// Section management
const selectedWidget = ref<string | null>(null)
const activeSection = ref<string | null>(null)

// Computed properties for determining which component to show
const isStatusSection = computed(() => ["PendingDelivery", "PendingUpload", "Delivered", "NotDelivered"].includes(activeSection.value || ""))

const getComponentForSection = computed(() => {
  switch (activeSection.value) {
    case "stores":
      return StoresView
    case "users":
      return UsersView
    case "drivers":
      return DriversView
    case "deliveries":
    case "PendingDelivery":
    case "PendingUpload":
    case "Delivered":
    case "NotDelivered":
      return DeliveriesView
    default:
      return null
  }
})

// Define widgets based on user role for more efficient rendering
const visibleWidgets = computed(() => {
  // const userRole = authStore.userInfo?.role;
  const widgets = []

  if (canView(["Admin"])) {
    widgets.push({ id: "stores", title: "Total Stores", count: counts.value.totalStores })
  }

  if (canView(["Admin", "Manager"])) {
    widgets.push({ id: "users", title: "Total Users", count: counts.value.totalUsers }, { id: "deliveries", title: "Total Deliveries", count: counts.value.totalDeliveries }, { id: "drivers", title: "Total Drivers", count: counts.value.totalDrivers })
  }

  if (canView(["Driver"])) {
    widgets.push(
      { id: "PendingDelivery", title: "Pending", count: counts.value.deliveries[DeliveryStatus.PendingDelivery] },
      { id: "PendingUpload", title: "Pending Upload", count: counts.value.deliveries[DeliveryStatus.PendingUpload] },
      { id: "Delivered", title: "Delivered", count: counts.value.deliveries[DeliveryStatus.Delivered] },
      { id: "NotDelivered", title: "Not Delivered", count: counts.value.deliveries[DeliveryStatus.NotDelivered] }
    )
  }

  return widgets
})

onMounted(async () => {
  fetchDashboardData()
  setInitialView()
})

const fetchDashboardData = async () => {
  // Reset error state
  errors.value = []

  loadingCounts.value = true
  counts.value = await dashboardApi.getCounts()
  loadingCounts.value = false
}

const canView = (roles: string[]): boolean => !!authStore.userInfo?.role && roles.includes(authStore.userInfo.role)

function toggleSection(section: string) {
 if (activeSection.value !== section) {
    selectedWidget.value = section
    activeSection.value = section
  }
}

function setInitialView() {
  const userRole = authStore.userInfo?.role

  if (userRole === "Driver") {
    selectedWidget.value = "PendingDelivery"
    activeSection.value = "PendingDelivery"
  } else if (userRole === "Manager") {
    selectedWidget.value = "users"
    activeSection.value = "users"
  } else {
    selectedWidget.value = "stores"
    activeSection.value = "stores"
  }
}

function getStatusValue(activeSection: string): number {
  const key = activeSection as (keyof typeof DeliveryStatus)
  return DeliveryStatus[key] || 0
}
</script>

<style scoped>
.dashboard-container {
  padding: 0px;
}

h1 {
  margin-bottom: 20px;
  color: #333;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.widgets-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
  gap: 20px;
  margin-bottom: 30px;
}

.widget {
  min-width: 200px;
  background-color: #e5f0ef;
  border: 1px solid #ddd;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.widget h6 {
  margin-top: 0;
  color: #555;
  margin-bottom: 10px;
  text-align: start;
}

.widget p {
  font-size: 1.5em;
  font-weight: bold;
  color: black;
  text-align: start;
  margin-bottom: 0;
}

.widget-loading {
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.active-widget {
  background-color: #0094de;
  border: 2px solid #0094de;
  transition: background-color 0.3s ease, border 0.3s ease;
}

.active-widget h6,
.active-widget p {
  color: white !important;
}

.loading-widget {
  opacity: 0.7;
}

.clickable {
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.clickable:hover {
  background-color: #eee;
}

.active-widget:hover {
  background-color: #045a53;
}

.content-section {
  margin-top: 30px;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;
}

.alert {
  padding: 12px 20px;
  border-radius: 4px;
}

.alert-warning {
  background-color: #fff3cd;
  border: 1px solid #ffecb5;
  color: #856404;
}

.error-list {
  margin-top: 8px;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .widgets-grid {
    justify-content: center;
  }

  .widget {
    min-width: 160px;
  }
}
</style>
